/**
 * Story Prep API Slice
 *
 * RTK Query endpoints for Scenario Library and Petals management.
 * (Events Prep was retired in RC10: its backend persisted nothing.)
 */

import { api } from '../api';

// ============================================
// Types
// ============================================

export type FlowerKey =
  | 'problem'
  | 'pain'
  | 'power'
  | 'product'
  | 'plan'
  | 'path'
  | 'place'
  | 'pushback'
  | 'promise';

export type CorePetalType =
  | 'story'
  | 'case_study'
  | 'irrefutable_logic'
  | 'media'
  | 'famous_quote';

export type ScenarioGoal = 'sell' | 'connect' | 'prove_expertise' | 'educate';
export type ScenarioDifficulty = 'beginner' | 'intermediate' | 'advanced';

export interface ScenarioTemplateDto {
  id: string;
  domainKey: string;
  scenarioKey: string;
  name: string;
  description: string;
  goal: ScenarioGoal;
  difficulty: ScenarioDifficulty;
}

export interface PetalDto {
  id: string;
  flower: FlowerKey;
  coreType: CorePetalType;
  title: string;
  content: string;
  domainKey?: string | null;
  scenarioKey?: string | null;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export type CreatePetalDto = Omit<PetalDto, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdatePetalDto = Partial<CreatePetalDto>;


// ============================================
// Constants / Labels
// ============================================

export const FLOWER_LABELS: Record<FlowerKey, string> = {
  problem: 'Problem',
  pain: 'Pain',
  power: 'Power',
  product: 'Product',
  plan: 'Plan',
  path: 'Path',
  place: 'Place',
  pushback: 'Pushback',
  promise: 'Promise',
};

export const FLOWER_DESCRIPTIONS: Record<FlowerKey, string> = {
  problem: 'The challenge or issue your audience faces',
  pain: 'The emotional impact of the problem',
  power: 'Your unique ability or expertise',
  product: 'Your solution or offering',
  plan: 'The roadmap or steps to success',
  path: 'The journey or process',
  place: 'Context, market, or setting',
  pushback: 'Objections and how to handle them',
  promise: 'The outcome or transformation you deliver',
};

export const CORE_PETAL_LABELS: Record<CorePetalType, string> = {
  story: 'Story',
  case_study: 'Case Study',
  irrefutable_logic: 'Irrefutable Logic',
  media: 'Media',
  famous_quote: 'Famous Quote',
};

export const SCENARIO_GOAL_LABELS: Record<ScenarioGoal, string> = {
  sell: 'Sell',
  connect: 'Connect',
  prove_expertise: 'Show Expertise',
  educate: 'Educate',
};

export const DOMAIN_LABELS: Record<string, string> = {
  tech_sales: 'Tech Sales',
  investor: 'Investor Relations',
  networking: 'Networking',
  coaching: 'Coaching',
  leadership: 'Leadership',
  marketing: 'Marketing',
  consulting: 'Consulting',
  real_estate: 'Real Estate',
  general: 'General',
};

export const FLOWER_KEYS: FlowerKey[] = [
  'problem',
  'pain',
  'power',
  'product',
  'plan',
  'path',
  'place',
  'pushback',
  'promise',
];

export const CORE_PETAL_TYPES: CorePetalType[] = [
  'story',
  'case_study',
  'irrefutable_logic',
  'media',
  'famous_quote',
];

// ============================================
// API Response Types
// ============================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API Slice
// ============================================

export const storyPrepApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ==================
    // Scenarios
    // ==================

    getScenarios: builder.query<ScenarioTemplateDto[], void>({
      query: () => '/scenarios',
      transformResponse: (response: ApiResponse<ScenarioTemplateDto[]>) => response.data,
      providesTags: ['Scenarios'],
    }),

    // ==================
    // Petals
    // ==================

    getPetals: builder.query<PetalDto[], void>({
      query: () => '/petals',
      transformResponse: (response: ApiResponse<PetalDto[]>) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Petals' as const, id })),
              { type: 'Petals', id: 'LIST' },
            ]
          : [{ type: 'Petals', id: 'LIST' }],
    }),

    createPetal: builder.mutation<PetalDto, CreatePetalDto>({
      query: (body) => ({
        url: '/petals',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<PetalDto>) => response.data,
      invalidatesTags: [{ type: 'Petals', id: 'LIST' }],
    }),

    updatePetal: builder.mutation<PetalDto, { id: string; data: UpdatePetalDto }>({
      query: ({ id, data }) => ({
        url: `/petals/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<PetalDto>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Petals', id },
        { type: 'Petals', id: 'LIST' },
      ],
    }),

    deletePetal: builder.mutation<void, string>({
      query: (id) => ({
        url: `/petals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Petals', id },
        { type: 'Petals', id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetScenariosQuery,
  useGetPetalsQuery,
  useCreatePetalMutation,
  useUpdatePetalMutation,
  useDeletePetalMutation,
} = storyPrepApi;
